"use client";

import { motion } from "framer-motion";
import { EASE, fadeUp, stagger, viewportOnce } from "@/lib/motion";

const values = [
  {
    number: "01",
    title: "Aus eigener Erfahrung",
    description:
      "ncrechner wurde von Studierenden für Studierende entwickelt. Wir kennen die Frustration des Bewerbungsverfahrens selbst.",
  },
  {
    number: "02",
    title: "Für alle vier Studiengänge",
    description:
      "Egal ob du dich für Humanmedizin, Zahnmedizin, Tiermedizin oder Pharmazie bewirbst: ncrechner unterstützt dich.",
  },
  {
    number: "03",
    title: "Offizielle Daten",
    description:
      "Unsere Berechnungen basieren auf den offiziellen Zulassungswerten aller öffentlichen deutschen Hochschulen, stets aktuell.",
  },
  {
    number: "04",
    title: "Ohne Hürden",
    description:
      "Noten eingeben, Ergebnisse erhalten. Keine Anmeldung, kein Abo, kein Stress: einfach loslegen.",
  },
];

export default function AboutSection() {
  return (
    <section id="about" className="cv-auto bg-paper py-24 lg:py-32 border-b border-line">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-14 lg:gap-20 items-start">

          {/* Left: sticky intro */}
          <motion.div
            variants={stagger(0.09)}
            initial="hidden"
            whileInView="visible"
            viewport={viewportOnce}
            className="lg:col-span-5 flex flex-col gap-6 lg:sticky lg:top-28"
          >
            <motion.p
              variants={fadeUp}
              className="flex items-center gap-3 text-xs font-bold uppercase tracking-widest text-ink-faint"
            >
              <span className="w-1.5 h-1.5 bg-accent shrink-0" aria-hidden="true" />
              Über uns
            </motion.p>

            <motion.h2
              variants={fadeUp}
              className="font-display font-bold text-4xl sm:text-5xl tracking-tight leading-[1.1] text-ink"
            >
              Von Studierenden, die das System selbst durchlaufen haben.
            </motion.h2>

            <motion.p variants={fadeUp} className="text-ink-soft leading-relaxed text-lg">
              Das Bewerbungsverfahren für medizinische Studiengänge ist komplex:
              verschiedene Quoten, unterschiedliche Berechnungsmethoden,
              unzählige Hochschulen. ncrechner macht dieses System verständlich
              und deine Chancen transparent.
            </motion.p>

            <motion.p
              variants={fadeUp}
              className="flex items-center gap-3 text-xs font-bold uppercase tracking-widest text-ink-faint pt-2"
            >
              <span className="h-px w-8 bg-line" aria-hidden="true" />
              futuredoctor GmbH · Frankfurt am Main
            </motion.p>
          </motion.div>

          {/* Right: numbered ruled list */}
          <div className="lg:col-span-7">
            {values.map(({ number, title, description }, i) => (
              <motion.div
                key={number}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, delay: i * 0.06, ease: EASE }}
                className="grid grid-cols-[3.5rem_1fr] gap-5 py-8 border-t border-line last:border-b"
              >
                <span className="font-display font-semibold text-accent tnum text-lg leading-none pt-1">
                  {number}
                </span>
                <div>
                  <h3 className="font-display font-bold text-xl text-ink leading-snug">
                    {title}
                  </h3>
                  <p className="text-ink-soft leading-relaxed mt-2 max-w-lg">{description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
